import { useTranslation } from "react-i18next";
import type { SchemaRegion } from "../../api/restaurantSchema";
import ScrollTabs from "../ScrollTabs";

/**
 * Регионите като сегментиран контрол: сива лента, избраният сегмент е бял и
 * вдигнат, с брояч „23 маси · 106 места" в кехлибарено; неактивните — с
 * приглушен брояч. Общ за „Конфигурация на маси", „Схема на заведението" и
 * „Резервации". Много региони → хоризонтален скрол (`ScrollTabs`), избраният
 * се доскролва във видимото. Височина 42px; `compact` — 36px (редът с деня и
 * „Нова резервация" в „Резервации").
 */
const RegionSegmentedTabs = ({
  regions,
  activeId,
  onChange,
  compact = false,
}: {
  regions: SchemaRegion[];
  activeId: number | null;
  onChange: (regionId: number) => void;
  compact?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex w-fit min-w-0 max-w-full rounded-[13px] bg-fill-2 p-1">
      <ScrollTabs activeKey={activeId} role="tablist" className="gap-1">
        {regions.map((region) => {
          const active = region.id === activeId;
          const tables = region.elements.filter((element) => element.kind === "table");
          const seatCount = tables.reduce((sum, table) => sum + (table.seats ?? 0), 0);
          return (
            <button
              key={region.id}
              type="button"
              role="tab"
              aria-selected={active}
              data-active={active}
              onClick={() => onChange(region.id)}
              className={`flex ${compact ? "h-7" : "h-[34px]"} shrink-0 cursor-pointer items-center gap-2 rounded-[9px] border-none px-3.5 transition-[background,box-shadow] duration-150 ${
                active ? "bg-surface shadow-[0_1px_3px_rgba(28,25,23,0.14)]" : "bg-transparent hover:bg-shade-1"
              }`}
            >
              <span
                className={`max-w-[180px] truncate text-[13.5px] leading-[1.3] ${
                  active ? "font-bold text-ink" : "font-semibold text-ink-2"
                }`}
              >
                {region.name}
              </span>
              <span
                className={`whitespace-nowrap text-[12px] font-semibold tabular-nums ${
                  active ? "text-brand-deep-ink" : "text-muted"
                }`}
              >
                {t("restaurantSchema.counts.tables", { count: tables.length })} ·{" "}
                {t("restaurantSchema.counts.seats", { count: seatCount })}
              </span>
            </button>
          );
        })}
      </ScrollTabs>
    </div>
  );
};

export default RegionSegmentedTabs;
